import React, { useState } from "react";
import {
  MdAdd,
  MdCancel,
  MdDelete,
  MdEdit,
  MdFilterAlt,
  MdSearch,
} from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";

import {
  removeFilter,
  clearFilters,
  setFilters,
  setSelectedFilterFields,
  setHasNewFilters,
} from "../../../data/redux/filterSlice";
import { Tooltip, Button, Badge } from "@mui/material";
import { moduleFilterFields } from "../../../data/constants/filterFields";
import {
  makeSelectFilters,
  makeSelectSelectedFilterFields,
} from "../../../data/redux/selectors/filterSelectors";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import api from "../../../../utils/api";
import {
  getUrlParams,
  setUrlParams,
} from "../../../../utils/helpers/setAndGetUrlParams";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { set } from "lodash";

const fetchOptions = async (searchKey, url) => {
  if (!searchKey) return [];
  const { data } = await api.get(url);
  // console.log("data is", data);

  return data.data; // Ensure API returns { options: [{ label, value }] }
};

const DynamicSelect = ({
  searchKey,
  searchValue,
  setSearchValue,
  setLabelValue,
  url,
  defaultOptions,
  valueKey,
  fieldType,
}) => {
  const { user } = useSelector((state) => state.auth);
  const { companyNameArr } = useSelector((state) => state.auth);
  const initialCompany = getUrlParams("companyid")
    ? getUrlParams("companyid")
    : user?.ofCompany?._id ||
      (companyNameArr.length > 0 ? companyNameArr[0].value : null);
  let company = user?.ofCompany?._id || initialCompany;
  // i want to replace some ofCompany keys in url
  url = url?.replace(":ofCompany", company);

  let { data: options = [], isLoading } = useQuery({
    queryKey: ["filter-options", searchKey],
    queryFn: () => fetchOptions(searchKey, url),
    enabled: !!searchKey && defaultOptions?.length == 0, // Only fetch when searchKey exists
  });

  if (defaultOptions?.length != 0) {
    options = defaultOptions;
  }

  return (
    <select
      className="form-select form-select-sm text-capitalize shadow-sm border-primary-subtle"
      value={searchValue}
      onChange={(e) => {
        const selectedOption = e.target.selectedOptions[0];
        const labelValue = selectedOption?.getAttribute("data-label");
        setSearchValue(e.target.value);

        if (fieldType === "select") {
          setLabelValue(labelValue);
        }
      }}
      disabled={isLoading}
    >
      <option value="" disabled>
        Select an option
      </option>
      {options?.map((option) => (
        <option
          key={option._id}
          value={option[valueKey] ? option[valueKey] : option.name}
          data-label={option?.name}
          className="text-capitalize"
        >
          {option.name?.split("-").join(" ")}
        </option>
      ))}
    </select>
  );
};

const CommonFilter = ({ module }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedFilterFields = useSelector(
    makeSelectSelectedFilterFields(module)
  );
  const filters = useSelector(makeSelectFilters(module));

  const [searchKey, setSearchKey] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [show, setShow] = useState(false);
  const [searchField, setSearchField] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editKey, setEditKey] = useState(null);
  const [labelValue, setLabelValue] = useState(null);

  const availableFilterFields = moduleFilterFields[module] || [];
  const filteredFields = availableFilterFields.filter((field) =>
    field.label.toLowerCase().includes(searchField.toLowerCase())
  );

  const handleAddFilter = () => {
    // console.log("searchKey", searchKey, searchValue);
    if (searchKey && searchValue) {
      const selectedField = availableFilterFields.find(
        (field) => field.key === searchKey
      );
      const label = selectedField ? selectedField.label : searchKey;
      const isQueryParam = selectedField?.isQueryParam;

      if (isEditing) {
        // Remove old filter before updating
        dispatch(removeFilter({ module, searchKey: editKey }));
      }

      if (selectedField?.type !== "select") {
        setLabelValue(null);
      }

      dispatch(
        setSelectedFilterFields({
          module,
          searchKey,
          searchValue,
          label,
          isQueryParam,
          labelValue: labelValue ? labelValue : searchValue,
          valueKey: selectedField?.valueKey,
        })
      );

      setSearchKey("");
      setSearchValue("");
      setIsEditing(false);
      setEditKey(null);
      setLabelValue(null);
    }
  };

  const handleEditFilter = (filter) => {
    setSearchKey(filter.searchKey);
    setSearchValue(filter.searchValue);
    setIsEditing(true);
    setEditKey(filter.searchKey);
  };

  const handleApplyFilter = () => {
    if (selectedFilterFields.length > 0) {
      dispatch(setFilters({ module, filters: selectedFilterFields }));
      // setShow(false);
    }
  };

  return (
    <>
      <Tooltip title="Apply Multiple Filters" placement="top">
        <button
          className="btn btn-outline-primary btn-sm "
          // style={{ padding: "0 0.30rem" }}
          onClick={() => setShow(!show)}
        >
          <Badge badgeContent={filters.length} color="secondary">
            <FilterAltIcon size={24} />
          </Badge>
        </button>
      </Tooltip>

      <div
        className={`filter-sidebar ${show ? "show" : ""}`}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "275px",
          height: "100vh",
          backgroundColor: "#fff",
          zIndex: 1050, // Ensure it's above the existing sidebar
          boxShadow: "3px 0 20px rgba(0, 0, 0, 0.15)",
          transform: show ? "translateX(0)" : "translateX(-100%)",
          transition: "transform 0.3s ease-in-out",
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div className="d-flex flex-column h-100">
          {/* Header */}
          <div className="px-2 border-bottom bg-light">
            <div className="d-flex justify-content-between align-items-center m-2">
              <h5 className="modal-title text-capitalize">
                Filter {module}{" "}
                {filters.length > 0 && (
                  <span className="bg-primary px-2 rounded-circle ">
                    {filters.length}
                  </span>
                )}
              </h5>
              <button
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShow(false)}
              >
                <MdCancel size={20} />
              </button>
            </div>
          </div>

          <div
            className="flex-grow-1 p-2"
            style={{ overflowY: "auto", maxHeight: "100vh" }}
          >
            {/* Search Field for Filtering Available Fields */}
            <div className="mb-3 position-relative">
              <div className="input-group input-group-sm">
                <span className="input-group-text bg-white border-end-0">
                  <MdSearch size={18} className="text-muted" />
                </span>
                <input
                  type="search"
                  className="form-control form-control-sm border-start-0 ps-0 shadow-none"
                  placeholder="Filter fields (Enter min 4 chars)..."
                  value={searchField}
                  onChange={(e) => {
                    const searchText = e.target.value.toLowerCase();
                    setSearchField(searchText);

                    // Apply filtering logic only if user has entered at least 4 characters
                    if (searchText.length >= 4) {
                      if (filteredFields.length > 0) {
                        setSearchKey(filteredFields[0].key);
                      }
                    }
                  }}
                  style={{
                    borderTopRightRadius: "0.375rem",
                    borderBottomRightRadius: "0.375rem",
                  }}
                />
              </div>
            </div>

            <div className="mb-3 border border-primary-subtle shadow">
              <div className="p-2">
                <h6 className="card-subtitle mb-2 text-muted">Select Filter</h6>

                <select
                  value={searchKey}
                  onChange={(e) => {
                    setSearchKey(e.target.value);
                    setSearchValue("");
                  }}
                  className="form-select form-select-sm mb-3 shadow-sm border-primary-subtle"
                >
                  <option value="" disabled>
                    Select a field
                  </option>
                  {filteredFields.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </select>

                <div className="mt-3">
                  {(() => {
                    const fieldType = availableFilterFields.find(
                      (field) => field.key === searchKey
                    )?.type;
                    const fieldData = availableFilterFields.find(
                      (field) => field.key === searchKey
                    );

                    switch (fieldType) {
                      case "string":
                        return (
                          <>
                            <div className="input-group input-group-sm mb-2">
                              <input
                                type="text"
                                className="form-control form-control-sm text-capitalize shadow-sm border-primary-subtle"
                                value={searchValue}
                                onChange={(e) => {
                                  setSearchValue(e.target.value);
                                  setSearchField("");
                                }}
                                placeholder="Enter value"
                              />
                            </div>
                            <button
                              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center w-100 mt-2"
                              onClick={handleAddFilter}
                              disabled={!searchKey || !searchValue}
                            >
                              <MdAdd size={18} className="me-1" />
                              {isEditing ? "Update Filter" : "Add Filter"}
                            </button>
                          </>
                        );

                      case "date":
                        return (
                          <div className="date-range-picker">
                            <div className="mb-2">
                              <label className="form-label small text-muted mb-1">
                                From date
                              </label>
                              <input
                                type="date"
                                className="form-control form-control-sm shadow-sm border-primary-subtle"
                                value={
                                  searchValue[`${fieldData.key}[gte]`] || ""
                                }
                                onChange={(e) =>
                                  setSearchValue({
                                    ...searchValue,
                                    [`${fieldData.key}[gte]`]: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <div className="mb-2">
                              <label className="form-label small text-muted mb-1">
                                To date
                              </label>
                              <input
                                type="date"
                                className="form-control form-control-sm shadow-sm border-primary-subtle"
                                value={
                                  searchValue[`${fieldData.key}[lte]`] || ""
                                }
                                onChange={(e) =>
                                  setSearchValue({
                                    ...searchValue,
                                    [`${fieldData.key}[lte]`]: e.target.value,
                                  })
                                }
                              />
                            </div>
                            <button
                              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center w-100 mt-2"
                              onClick={handleAddFilter}
                              disabled={
                                !searchKey ||
                                !searchValue ||
                                (!searchValue[`${fieldData.key}[gte]`] &&
                                  !searchValue[`${fieldData.key}[lte]`])
                              }
                            >
                              <MdAdd size={18} className="me-1" />
                              {isEditing
                                ? "Update Date Range"
                                : "Add Date Range"}
                            </button>
                          </div>
                        );

                      case "select":
                        return (
                          <>
                            <DynamicSelect
                              searchKey={searchKey}
                              searchValue={searchValue}
                              setSearchValue={setSearchValue}
                              setLabelValue={setLabelValue}
                              url={fieldData?.url}
                              defaultOptions={fieldData.options}
                              valueKey={fieldData.valueKey}
                              fieldType={fieldData?.type || ""}
                            />
                            <button
                              className="btn btn-primary btn-sm d-flex align-items-center justify-content-center w-100 mt-2"
                              onClick={handleAddFilter}
                              disabled={!searchKey || !searchValue}
                            >
                              <MdAdd size={18} className="me-1" />
                              {isEditing ? "Update Selection" : "Add Selection"}
                            </button>
                          </>
                        );

                      default:
                        return null;
                    }
                  })()}
                </div>
              </div>
            </div>

            {/* Applied Filters Section */}
            {selectedFilterFields.length > 0 && (
              <div className="mt-3">
                <h6 className="d-flex align-items-center text-muted mb-3">
                  <span className="me-2">Applied Filters</span>
                  <span className="badge bg-primary rounded-pill">
                    {selectedFilterFields.length}
                  </span>
                </h6>

                <div className="filter-items">
                  {selectedFilterFields.map((filter, index) => {
                    return (
                      <div key={index} className="card mb-2 border-0 shadow-lg">
                        <div className="card-body p-2">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <h6 className="card-title mb-0 text-primary fw-bold fs-6">
                              <small>{filter.label}</small>
                            </h6>
                            <div className="btn-group btn-group-sm">
                              <button
                                className="btn btn-outline-primary btn-sm border-0"
                                onClick={() => handleEditFilter(filter)}
                                title="Edit filter"
                              >
                                <MdEdit size={16} />
                              </button>
                              <button
                                className="btn btn-outline-danger btn-sm border-0"
                                onClick={() =>
                                  dispatch(
                                    removeFilter({
                                      module,
                                      searchKey: filter.searchKey,
                                    })
                                  )
                                }
                                title="Remove filter"
                              >
                                <MdDelete size={16} />
                              </button>
                            </div>
                          </div>

                          <div className="filter-value text-capitalize">
                            {Boolean(typeof filter.searchValue === "object") ? (
                              <div className="d-flex flex-wrap gap-1">
                                {Object.keys(filter.searchValue).map((key) => {
                                  const isDateField =
                                    key.includes("[gte]") ||
                                    key.includes("[lte]");
                                  const labelText = key.includes("[gte]")
                                    ? "From: "
                                    : "To: ";

                                  return (
                                    <span
                                      key={key}
                                      className="badge bg-light text-dark border"
                                      style={{ fontSize: "0.75rem" }}
                                    >
                                      {isDateField && (
                                        <small className="text-muted">
                                          {labelText}
                                        </small>
                                      )}
                                      {filter.searchValue[key]}
                                    </span>
                                  );
                                })}
                              </div>
                            ) : (
                              <span className="small text-dark">
                                {filter.labelValue
                                  ? filter.labelValue
                                  : filter.searchValue?.split("-").join(" ")}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Empty state when no filters */}
            {selectedFilterFields.length === 0 && (
              <div className="text-center py-4 text-muted">
                <div className="mb-2">
                  <MdFilterAlt size={40} className="text-muted opacity-50" />
                </div>
                <p className="mb-0">No filters added yet</p>
                <small>Select a field and add your first filter</small>
              </div>
            )}
          </div>

          {/* Footer Actions */}
          <div
            className=" border-top bg-light"
            style={{ boxShadow: "0 -2px 10px rgba(0,0,0,0.05)" }}
          >
            <div className="d-flex justify-content-between align-items-center">
              <div className="p-2 border-top d-flex gap-2 mt-auto w-100">
                <button
                  className="btn btn-outline-primary flex-grow-1"
                  onClick={() => {
                    dispatch(clearFilters({ module }));
                    setUrlParams(setSearchParams, "page", 1);
                  }}
                >
                  Clear All
                </button>
                <button
                  className="btn btn-primary flex-grow-1"
                  onClick={handleApplyFilter}
                >
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommonFilter;
